// tog‘a #2065 W1 (2026-09-15): qurilma-skanerni klaviatura oqimidan ajratish — butun ilova bo‘yicha BITTA tinglovchi (window, capture fazasi).
// Skaner klaviatura kabi yozadi: tez oqim (≥4 belgi, har oraliq ~35 ms dan tez) + Enter/Tab. Qo‘lda yozish sekin — skan emas.
// Skan tasdiqlansa: terminator to‘xtatiladi (forma yuborilmaydi, Select variant tanlamaydi) va fokusdagi maydon qiymati skandan oldingi holatiga qaytariladi.
// `[data-scan-owner]` ichidagi maydonlar (POS, kassa, zavod qabuli, zakaz katalogi, tanlash oynalari) o‘z skanini o‘zi o‘qiydi — aralashilmaydi.
// Belgi `e.code` dan olinadi: rus/o‘zbek tartibida `e.key` kirill harf beradi, skaner esa lotin kod yuboradi.

export type ScanHandler = (code: string) => void

const MIN_LEN = 4
const DEFAULT_GAP_MS = 35
const MAX_LEN = 128

/** oraliq chegarasi — sekin Bluetooth skanerlar uchun localStorage("scanGapMs") bilan 15–200 ms oralig‘ida o‘zgartiriladi */
export function scanGapMs(): number {
  try {
    const v = Number(localStorage.getItem("scanGapMs"))
    if (v >= 15 && v <= 200) return v
  } catch (e) {}
  return DEFAULT_GAP_MS
}

function charOf(e: KeyboardEvent): string | null {
  const c = e.code || ""
  if (/^Key[A-Z]$/.test(c)) {
    const ch = c.slice(3)
    return e.shiftKey ? ch : ch.toLowerCase()
  }
  if (/^Digit[0-9]$/.test(c)) return e.shiftKey ? null : c.slice(5)
  if (/^Numpad[0-9]$/.test(c)) return c.slice(6)
  switch (c) {
    case "Minus":
      return e.shiftKey ? "_" : "-"
    case "NumpadSubtract":
      return "-"
    case "Period":
    case "NumpadDecimal":
      return e.shiftKey ? null : "."
    case "Slash":
    case "NumpadDivide":
      return e.shiftKey ? null : "/"
    case "NumpadAdd":
      return "+"
    case "Equal":
      return e.shiftKey ? "+" : "="
    case "Space":
      return " "
  }
  // TSD / noma’lum qurilma: code bo‘sh bo‘lsa — bitta belgili key
  if (!c && e.key && e.key.length === 1) return e.key
  return null
}

const isEditable = (el: Element | null): el is HTMLInputElement | HTMLTextAreaElement =>
  !!el && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)

const isOwned = (el: Element | null) => !!(el && (el as HTMLElement).closest && (el as HTMLElement).closest("[data-scan-owner]"))

interface Buf {
  chars: string[]
  times: number[]
  el: HTMLInputElement | HTMLTextAreaElement | null
  value: string | null
  selStart: number | null
  selEnd: number | null
}
const emptyBuf = (): Buf => ({ chars: [], times: [], el: null, value: null, selStart: null, selEnd: null })

function restore(b: Buf) {
  const el = b.el
  if (!el || b.value === null || !el.isConnected) return
  try {
    const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
    const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set
    if (setter && el.value !== b.value) {
      setter.call(el, b.value)   // React boshqaradigan maydon: native setter + input hodisasi — onChange eski qiymat bilan chaqiriladi
      el.dispatchEvent(new Event("input", { bubbles: true }))
    }
    if (b.selStart !== null && typeof el.setSelectionRange === "function") el.setSelectionRange(b.selStart, b.selEnd ?? b.selStart)
  } catch (e) {}
}

/** Butun ilovaga bitta tinglovchi o‘rnatadi. Qaytgan funksiya tinglovchini olib tashlaydi.
 *  tog'a #2537 (2026-09-24): `shouldCapture(active)` false qaytarsa skan USHLANMAYDI — kod fokusdagi maydonda qoladi
 *  (tovar kartasidagi «Штрих-код», qidiruv, filtrlar). Maydon forma ichida bo'lsa Enter to'xtatiladi (forma o'zi saqlanmasin),
 *  qidiruvda esa Enter o'tadi (kod bilan qidiradi). */
export function startScanDetector(onScan: ScanHandler, shouldCapture?: (active: Element | null) => boolean): () => void {
  let buf = emptyBuf()
  const reset = () => {
    buf = emptyBuf()
  }

  const handler = (e: KeyboardEvent) => {
    if (e.isComposing || e.keyCode === 229 || e.repeat || e.ctrlKey || e.altKey || e.metaKey) {
      reset()
      return
    }
    const active = document.activeElement
    if (isOwned(active)) {
      reset()
      return
    }
    const now = performance.now()
    const gap = scanGapMs()

    if (e.key === "Enter" || e.key === "Tab" || e.code === "NumpadEnter") {
      const n = buf.chars.length
      const lastGap = n ? now - buf.times[n - 1] : Infinity
      if (n >= MIN_LEN && lastGap <= Math.max(gap * 3, 100)) {
        if (shouldCapture && !shouldCapture(active)) {
          if (e.key !== "Tab" && active && (active as HTMLElement).closest && (active as HTMLElement).closest("form")) e.preventDefault()
          reset()
          return
        }
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        restore(buf)
        const code = buf.chars.join("").trim()
        reset()
        if (code.length >= MIN_LEN) {
          try {
            onScan(code)
          } catch (err) {
            console.error("scan handler", err)
          }
        }
      } else reset()
      return
    }
    if (e.key === "Shift" || e.key === "CapsLock") return   // alohida Shift oqimni buzmaydi (skaner katta harfni Shift bilan yuboradi)

    const ch = charOf(e)
    if (ch === null) {
      reset()
      return
    }
    const n = buf.chars.length
    if (n && now - buf.times[n - 1] > gap) reset()   // sekin — bu qo‘lda yozish, yangi oqim shu belgidan boshlanadi
    if (!buf.chars.length) {
      // birinchi belgi keydown da hali maydonga tushmagan — qiymat skandan OLDINGI holat
      if (isEditable(active)) {
        buf.el = active
        buf.value = active.value
        try {
          buf.selStart = active.selectionStart
          buf.selEnd = active.selectionEnd
        } catch (err) {
          buf.selStart = null
          buf.selEnd = null
        }
      }
    }
    buf.chars.push(ch)
    buf.times.push(now)
    if (buf.chars.length > MAX_LEN) reset()
  }

  window.addEventListener("keydown", handler, true)
  return () => window.removeEventListener("keydown", handler, true)
}
